(() => {
  const formatMoney = (cents, currency) => {
    const numericCents = Number(cents || 0);
    const value = numericCents / 100;
    const locale = document.documentElement.lang || 'nl-NL';

    if (numericCents % 100 === 0) {
      const whole = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
      return `${whole},-`;
    }

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency
    }).format(value);
  };

  const initializeGallery = (gallery) => {
    if (!gallery || gallery.dataset.galleryReady === 'true') return;
    gallery.dataset.galleryReady = 'true';

    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    const previous = gallery.querySelector('[data-gallery-prev]');
    const next = gallery.querySelector('[data-gallery-next]');
    const current = gallery.querySelector('[data-gallery-current]');
    if (slides.length < 2) return;

    let activeIndex = 0;

    const show = (index) => {
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        slide.hidden = slideIndex !== activeIndex;
      });
      thumbs.forEach((thumb, thumbIndex) => {
        thumb.setAttribute('aria-current', thumbIndex === activeIndex ? 'true' : 'false');
      });
      if (current) current.textContent = String(activeIndex + 1);
      thumbs[activeIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    };

    previous?.addEventListener('click', () => show(activeIndex - 1));
    next?.addEventListener('click', () => show(activeIndex + 1));
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => show(Number(thumb.dataset.galleryIndex || 0)));
    });
    gallery.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') show(activeIndex - 1);
      if (event.key === 'ArrowRight') show(activeIndex + 1);
    });
  };

  const initializeProduct = (section) => {
    if (section.dataset.productReady === 'true') return;
    section.dataset.productReady = 'true';

    initializeGallery(section.querySelector('[data-product-gallery]'));

    const select = section.querySelector('[data-variant-select]');
    const form = section.querySelector('.pw-product__form');
    const button = section.querySelector('[data-add-to-cart]');
    const price = section.querySelector('[data-variant-price]');
    const compare = section.querySelector('[data-variant-compare]');
    const sku = section.querySelector('[data-variant-sku]');
    const barcode = section.querySelector('[data-variant-barcode]');
    const availability = section.querySelector('[data-variant-availability]');
    const feedback = section.querySelector('[data-cart-feedback]');
    const serviceContainer = section.querySelector('[data-product-services]');
    const serviceInputs = [...section.querySelectorAll('[data-service-variant]')];
    const currency = section.dataset.currency || 'EUR';
    const isEol = section.dataset.eol === 'true';
    const isPriceRequest = section.dataset.priceRequest === 'true';
    const requiresDelivery = section.dataset.requiresDelivery === 'true';

    let variants = [];
    try {
      variants = JSON.parse(section.querySelector('[data-variant-data]')?.textContent || '[]');
    } catch (error) {
      console.warn('Variantgegevens konden niet worden gelezen.', error);
    }

    const getCurrentVariant = () => {
      const id = select?.value || form?.querySelector('[name="id"]')?.value;
      return variants.find((item) => String(item.id) === String(id)) || variants[0];
    };

    const getSelectedServices = () => serviceInputs
      .filter((input) => input.checked && input.value)
      .map((input) => ({
        id: Number(input.value),
        price: Number(input.dataset.servicePrice || 0),
        title: input.dataset.serviceTitle || '',
        group: input.dataset.serviceGroup || 'service'
      }));

    const updateServiceSummary = () => {
      if (!serviceContainer) return;
      const variant = getCurrentVariant();
      const productPrice = Number(variant?.price || serviceContainer.dataset.productBasePrice || 0);
      const optionsPrice = getSelectedServices().reduce((sum, service) => sum + service.price, 0);

      serviceContainer.querySelector('[data-service-product-price]')?.replaceChildren(formatMoney(productPrice, currency));
      serviceContainer.querySelector('[data-service-options-price]')?.replaceChildren(formatMoney(optionsPrice, currency));
      serviceContainer.querySelector('[data-service-total-price]')?.replaceChildren(formatMoney(productPrice + optionsPrice, currency));
    };

    const updateVariant = () => {
      const variant = getCurrentVariant();
      if (!variant) return;

      const variantIsPriceRequest = Number(variant.price) <= 0;
      if (select && variantIsPriceRequest !== isPriceRequest) {
        const variantUrl = new URL(window.location.href);
        variantUrl.searchParams.set('variant', variant.id);
        window.location.assign(variantUrl);
        return;
      }

      if (price) {
        price.textContent = isPriceRequest
          ? section.dataset.labelPriceRequest
          : formatMoney(variant.price, currency);
      }
      if (sku) sku.textContent = variant.sku || '—';
      if (barcode) barcode.textContent = variant.barcode || '—';

      if (compare) {
        const hasCompare = Number(variant.compareAtPrice) > Number(variant.price);
        compare.hidden = !hasCompare;
        compare.textContent = hasCompare ? formatMoney(variant.compareAtPrice, currency) : '';
      }

      const purchasable = Boolean(variant.available) && !isEol && !isPriceRequest;
      if (button) {
        button.disabled = !purchasable;
        button.textContent = isEol
          ? section.dataset.labelEol
          : purchasable
            ? section.dataset.labelAdd
            : section.dataset.labelUnavailable;
      }

      const quantity = section.querySelector('[name="quantity"]');
      if (quantity) quantity.disabled = !purchasable;

      if (availability && !isEol) {
        availability.textContent = isPriceRequest
          ? section.dataset.labelRequestAvailability
          : purchasable
            ? section.dataset.labelAvailable
            : section.dataset.labelUnavailable;
        availability.classList.toggle('pw-availability--unavailable', !purchasable);
      }

      if (select) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      updateServiceSummary();
    };

    select?.addEventListener('change', updateVariant);
    serviceInputs.forEach((input) => input.addEventListener('change', updateServiceSummary));

    form?.addEventListener('submit', async (event) => {
      const services = getSelectedServices();
      if (!serviceContainer || services.length === 0 || isEol) return;

      event.preventDefault();
      const variant = getCurrentVariant();
      const quantity = Math.max(1, Number(section.querySelector('[name="quantity"]')?.value || 1));
      if (!variant?.available || !button) return;

      const bundleKey = `pw-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const serviceTitles = services.map((service) => service.title).join(' • ');
      const productProperties = {
        _pw_bundle_key: bundleKey,
        _pw_services: serviceTitles
      };
      if (requiresDelivery) productProperties._pw_delivery_required = 'true';

      const items = [
        {
          id: Number(variant.id),
          quantity,
          properties: productProperties
        },
        ...services.map((service) => ({
          id: service.id,
          quantity,
          properties: {
            _pw_bundle_key: bundleKey,
            _pw_service_for: section.dataset.productTitle,
            _pw_service_variant: variant.sku || variant.title || '',
            _pw_service_group: service.group
          }
        }))
      ];

      const originalLabel = button.textContent;
      button.disabled = true;
      button.textContent = section.dataset.labelAdding;
      if (feedback) feedback.hidden = true;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Toevoegen mislukt.');

        const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await cartResponse.json();
        document.querySelectorAll('[data-cart-count]').forEach((count) => {
          count.textContent = cart.item_count;
        });

        button.textContent = section.dataset.labelAdded;
        if (feedback) {
          feedback.hidden = false;
          feedback.innerHTML = `<span>${section.dataset.labelAdded}</span> <a href="${root}cart">Bekijk winkelwagen</a>`;
        }
      } catch (error) {
        button.textContent = originalLabel;
        if (feedback) {
          feedback.hidden = false;
          feedback.textContent = error instanceof Error ? error.message : 'Toevoegen mislukt.';
        }
      } finally {
        window.setTimeout(() => {
          button.disabled = !getCurrentVariant()?.available || isEol || isPriceRequest;
          if (button.textContent === section.dataset.labelAdded) button.textContent = section.dataset.labelAdd;
        }, 1800);
      }
    });

    updateVariant();
  };

  document.querySelectorAll('[data-product-section]').forEach(initializeProduct);
})();
